'use client';

/**
 * OrganizationProjectsTab
 *
 * Projects tab content for organization page.
 * Shows list of projects belonging to this organization.
 */

import {
  Alert,
  AlertDescription,
  Badge,
  Button,
  DataTable,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Inline,
  Stack,
  Text,
} from '@cortex/ui';
import { useNavigate } from '@tanstack/react-router';
import { MoreHorizontal, Pencil, Plus, Trash2 } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';

import { useApi } from '@/hooks/use-api';
import { formatDate } from '@/lib/admin-utils';

import { CreateProjectWizard } from './CreateProjectWizard';
import { DeleteProjectDialog } from './DeleteProjectDialog';
import { EditProjectModal } from './EditProjectModal';

interface Project {
  id: string;
  name: string;
  slug: string;
  status: string;
  created_at: string;
}

interface OrganizationProjectsTabProps {
  organizationId: string;
}

export function OrganizationProjectsTab({
  organizationId,
}: OrganizationProjectsTabProps) {
  const navigate = useNavigate();
  const getApi = useApi();
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showWizard, setShowWizard] = useState(false);
  const [organizations, setOrganizations] = useState<
    { id: string; name: string; slug: string }[]
  >([]);
  const [editProject, setEditProject] = useState<Project | null>(null);
  const [deleteProject, setDeleteProject] = useState<Project | null>(null);

  // Fetch projects for this organization
  const fetchProjects = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const api = await getApi();
      const response = await api.adminProjects.index.$get({
        query: { organizationId },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch projects');
      }

      const json = await response.json();
      if ('data' in json) {
        const data = json.data as unknown as { items: Project[] };
        setProjects(data.items || []);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load projects');
    } finally {
      setLoading(false);
    }
  }, [organizationId, getApi]);

  // Fetch organizations for wizard
  const fetchOrganizations = useCallback(async () => {
    try {
      const api = await getApi();
      const response = await api.admin.organizations.$get();
      const json = await response.json();
      if ('data' in json) {
        const data = json.data as unknown as {
          items: { id: string; name: string; slug: string }[];
        };
        setOrganizations(data.items || []);
      }
    } catch {
      // Silently handle fetch failure
    }
  }, [getApi]);

  useEffect(() => {
    fetchProjects();
    fetchOrganizations();
  }, [fetchProjects, fetchOrganizations]);

  // Get status badge variant
  const getStatusVariant = (
    status: string
  ): 'success' | 'secondary' | 'info' => {
    switch (status) {
      case 'active':
        return 'success';
      case 'archived':
        return 'secondary';
      default:
        return 'info';
    }
  };

  // Table columns
  const columns = [
    {
      key: 'name',
      header: 'Name',
      sortable: true,
      render: (project: Project) => (
        <Stack gap="none">
          <Text weight="medium">{project.name}</Text>
          <Text color="muted" size="sm">
            {project.slug}
          </Text>
        </Stack>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      sortable: true,
      render: (project: Project) => (
        <Badge variant={getStatusVariant(project.status)}>
          {project.status}
        </Badge>
      ),
    },
    {
      key: 'created_at',
      header: 'Created',
      sortable: true,
      render: (project: Project) => (
        <Text size="sm" color="muted">
          {formatDate(project.created_at)}
        </Text>
      ),
    },
    {
      key: 'actions',
      header: '',
      render: (project: Project) => (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              onClick={(e) => e.stopPropagation()}
            >
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem
              onClick={(e) => {
                e.stopPropagation();
                setEditProject(project);
              }}
            >
              <Pencil className="mr-2 h-4 w-4" />
              Edit
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={(e) => {
                e.stopPropagation();
                setDeleteProject(project);
              }}
              className="text-destructive"
            >
              <Trash2 className="mr-2 h-4 w-4" />
              Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      ),
    },
  ];

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertDescription>{error}</AlertDescription>
      </Alert>
    );
  }

  return (
    <Stack gap="md">
      <Inline justify="between">
        <Stack gap="none">
          <Text weight="semibold">Projects</Text>
          <Text color="muted" size="sm">
            {projects.length} {projects.length === 1 ? 'project' : 'projects'}
          </Text>
        </Stack>
        <Button onClick={() => setShowWizard(true)} className="gap-2">
          <Plus className="h-4 w-4" />
          New Project
        </Button>
      </Inline>

      <DataTable
        columns={columns}
        data={projects}
        loading={loading}
        emptyMessage="No projects found for this organization"
        onRowClick={(project) =>
          navigate({ to: '/admin/projects/$id', params: { id: project.id } })
        }
      />

      {/* Create Project Wizard */}
      <CreateProjectWizard
        open={showWizard}
        onClose={() => setShowWizard(false)}
        organizations={organizations}
        preselectedOrganizationId={organizationId}
        onSuccess={() => {
          setShowWizard(false);
          fetchProjects();
        }}
      />

      {/* Edit Project Modal */}
      {editProject && (
        <EditProjectModal
          open={!!editProject}
          onClose={() => setEditProject(null)}
          project={editProject}
          onSuccess={() => {
            setEditProject(null);
            fetchProjects();
          }}
        />
      )}

      {/* Delete Project Dialog */}
      {deleteProject && (
        <DeleteProjectDialog
          open={!!deleteProject}
          onClose={() => setDeleteProject(null)}
          project={deleteProject}
          onSuccess={() => {
            setDeleteProject(null);
            fetchProjects();
          }}
        />
      )}
    </Stack>
  );
}
